import { normalizeAiOutputFormat } from './index.js';
import { resolveFormatAxes } from './aiFormatAxes.js';
function masterIdOfField(field) {
    if (field.kind === 'code-list-string' || field.kind === 'enum-array') {
        return field.source.type === 'master' ? field.source.masterId : null;
    }
    return null;
}
/**
 * 実効スキーマから机上調査の調査項目 (分類軸) を列挙する。
 *   1. `resolveFormatAxes` (classificationAxes 明示 or fields[] からの導出)
 *   2. object-array (分類配列) の itemFields のうち code-list-string / enum-array (評価列など、
 *      分類 1 件ごとの列) を軸として追加 (codeField 重複は除く)
 */
export function listSurveyAxes(schema) {
    if (!schema)
        return [];
    const out = [];
    const seen = new Set();
    for (const r of resolveFormatAxes(schema)) {
        const codeField = r.axis.codeField || r.axis.nameField || '';
        if (!codeField || seen.has(codeField))
            continue;
        seen.add(codeField);
        out.push({
            key: r.axis.key ?? codeField,
            label: r.axis.label || codeField,
            codeField,
            nameField: r.axis.nameField ?? '',
            masterId: r.axis.masterId ?? null,
            role: r.axis.role,
            explicit: r.explicit,
            axis: r.axis,
        });
    }
    const normalized = normalizeAiOutputFormat(schema);
    for (const field of normalized.fields ?? []) {
        if (field.kind !== 'object-array')
            continue;
        for (const item of field.itemFields ?? []) {
            if (item.kind !== 'code-list-string' && item.kind !== 'enum-array')
                continue;
            if (seen.has(item.key))
                continue;
            seen.add(item.key);
            const masterId = masterIdOfField(item);
            const axis = {
                key: item.key,
                codeField: item.key,
                nameField: '',
                label: item.label || item.key,
                ...(masterId ? { masterId } : {}),
            };
            out.push({
                key: item.key,
                label: axis.label,
                codeField: item.key,
                nameField: '',
                masterId,
                role: undefined,
                explicit: false,
                axis,
            });
        }
    }
    return out;
}
/** 地区上書き → テンプレ既定 の順で「調査項目」設定をマージする (未設定項目はテンプレを継承)。 */
export function resolveStreetSurveyDisplay(template, override) {
    const pick = (k) => {
        const o = override?.[k];
        if (o !== undefined && o !== null && !(Array.isArray(o) && o.length === 0))
            return o;
        return template?.[k];
    };
    // docs/27 §15-3: 解析パイプライン v2 の設定は項目ごとに地区上書き → テンプレ既定 (null / 未設定は
    // テンプレートに従う)。循環 import を避けるため、重ね方は streetSurveyPipeline.ts の
    // mergeStreetSurveyExtractionDef と同じ規則をここに持つ (テストで一致を確認)。
    const extractionEntries = { ...(template?.extraction ?? {}) };
    for (const [k, v] of Object.entries(override?.extraction ?? {})) {
        if (v !== undefined && v !== null)
            extractionEntries[k] = v;
    }
    const extraction = template?.extraction || override?.extraction
        ? extractionEntries
        : undefined;
    const resultEntries = { ...(template?.result ?? {}) };
    for (const [k, v] of Object.entries(override?.result ?? {})) {
        if (typeof v === 'string' && v.trim())
            resultEntries[k] = v;
    }
    const result = template?.result || override?.result
        ? resultEntries
        : undefined;
    return {
        legendAxisKey: pick('legendAxisKey') ?? null,
        aggregateAxisKeys: pick('aggregateAxisKeys') ?? [],
        evaluationField: pick('evaluationField') ?? null,
        listFieldKeys: pick('listFieldKeys') ?? [],
        ...(extraction ? { extraction } : {}),
        ...(result ? { result } : {}),
    };
}
/**
 * 机上調査の設定を 3 層で重ねる (docs/27 §17-2): 出力フォーマットの `schema.streetSurvey` (移行期間の既定)
 * ＜ 地区に適用した机上調査テンプレート ＜ 地区の上書き。空欄・未設定の項目は下の層に従う。
 */
export function resolveStreetSurveyDisplayLayers(formatDefault, template, override) {
    return resolveStreetSurveyDisplay(resolveStreetSurveyDisplay(formatDefault, template), override);
}
/** 慣例: key が `role_type` の軸 (docs/25 §3-1 の役割分類) を「凡例軸」の自動既定にする。 */
const LEGEND_AUTO_KEY = 'role_type';
/** 凡例軸の既定 key: 設定 → role_type → primary → 先頭。 */
export function resolveLegendAxisKey(axes, display) {
    if (display.legendAxisKey && axes.some((a) => a.key === display.legendAxisKey)) {
        return display.legendAxisKey;
    }
    return (axes.find((a) => a.key === LEGEND_AUTO_KEY)?.key ??
        axes.find((a) => a.role === 'primary')?.key ??
        axes[0]?.key ??
        null);
}
/** 集計軸の既定 key 一覧: 設定 → primary → 先頭 (評価列は除く)。 */
export function resolveAggregateAxisKeys(axes, display, evaluationField) {
    const configured = (display.aggregateAxisKeys ?? []).filter((k) => axes.some((a) => a.key === k));
    if (configured.length > 0)
        return configured;
    const primary = axes.find((a) => a.role === 'primary' && a.codeField !== evaluationField);
    const first = axes.find((a) => a.codeField !== evaluationField);
    const key = primary?.key ?? first?.key ?? null;
    return key ? [key] : [];
}
/**
 * 極性ラベルの推定 (明示設定の無いマスタ / assessment 値のフォールバック)。
 * **暫定**: M2 の「評価の極性」(`AiCategoryMasterRow.polarity`) を明示すれば使われない。語の一致のみで
 * 判定し、記号 (+ / -) やコード風の値は判定しない (誤判定を避けるため null = 評価なし)。
 */
export function inferPolarityFromLabel(label) {
    const s = label.trim().toLowerCase();
    if (!s)
        return null;
    if (/中立|neutral|どちらでもない|どちらでもなく|普通/.test(s))
        return 'neutral';
    if (/ポジ|positive|良い|よい|いいね|好ましい|ホッと|快適|安心|プラス|良好/.test(s))
        return 'positive';
    if (/ネガ|negative|悪い|わるい|もったいない|不安|不快|ハッと|危険|マイナス|課題/.test(s)) {
        return 'negative';
    }
    return null;
}
/**
 * 行の名称がすべて評価の語で、肯定と否定の両方を含むマスタか (例: ポジティブ / ネガティブ / 中立、
 * いいね / もったいない)。極性を明示していないマスタで、語から極性を推定してよいかの判定に使う。
 */
export function isPolarityLabelRows(rows) {
    const list = rows ?? [];
    if (list.length === 0)
        return false;
    const polarities = list.map((r) => inferPolarityFromLabel(r.category ?? r.categoryCd ?? ''));
    if (polarities.some((p) => p === null))
        return false;
    return polarities.includes('positive') && polarities.includes('negative');
}
/**
 * マスタの行の極性。明示 (`polarity`) を優先し、マスタのどの行にも明示が無いときだけ、推定してよいマスタ
 * (`allowInference`) に限って名称から推定する。
 */
export function masterRowPolarity(master, row, allowInference) {
    if (row.polarity)
        return row.polarity;
    if (!allowInference)
        return null;
    if ((master.rows ?? []).some((r) => r.polarity))
        return null;
    return inferPolarityFromLabel(row.category ?? row.categoryCd ?? '');
}
export const POLARITY_DEFAULT_COLORS = {
    positive: '#10B981',
    negative: '#EF4444',
    neutral: '#9CA3AF',
};
export const POLARITY_LABELS = {
    positive: 'ポジティブ',
    negative: 'ネガティブ',
    neutral: '中立',
};
function rowMatches(row, value) {
    const v = value.trim();
    if (!v)
        return false;
    return (row.categoryCd === v ||
        row.category === v ||
        (row.majorCd ? `${row.majorCd}-${row.categoryCd}` === v : false) ||
        v.endsWith(`-${row.categoryCd}`));
}
function buildMasterEvaluation(field, label, master, source) {
    // 語の推定は、評価の列として明示したマスタか、行の名称がすべて評価の語のマスタに限る。
    const allowInference = source === 'configured' || isPolarityLabelRows(master.rows);
    const findRow = (value) => typeof value === 'string' ? master.rows?.find((r) => rowMatches(r, value)) : undefined;
    const polarityFromMaster = (value) => {
        const row = master.rows?.find((r) => rowMatches(r, value));
        return row ? masterRowPolarity(master, row, allowInference) : null;
    };
    const polarityLabels = { ...POLARITY_LABELS };
    const seen = new Set();
    for (const row of master.rows ?? []) {
        const p = masterRowPolarity(master, row, allowInference);
        const name = (row.category ?? '').trim();
        if (p && name && !seen.has(p)) {
            polarityLabels[p] = name;
            seen.add(p);
        }
    }
    return {
        field,
        label,
        masterId: master.id ?? null,
        source,
        polarityLabels,
        polarityOf: (value) => (typeof value === 'string' ? polarityFromMaster(value) : null),
        colorOf: (value) => {
            const row = findRow(value);
            const polarity = typeof value === 'string' ? polarityFromMaster(value) : null;
            return (row?.color ??
                (row && master.colorMap?.[row.majorCd ?? '']
                    ? master.colorMap[row.majorCd ?? '']
                    : undefined) ??
                (polarity ? POLARITY_DEFAULT_COLORS[polarity] : POLARITY_DEFAULT_COLORS.neutral));
        },
        labelOf: (value) => findRow(value)?.category ?? String(value ?? ''),
    };
}
function buildLabelEvaluation(field, label, positiveLabel, negativeLabel, source) {
    const polarityOf = (value) => {
        if (typeof value !== 'string')
            return null;
        const v = value.trim();
        if (!v)
            return null;
        if (positiveLabel && v === positiveLabel)
            return 'positive';
        if (negativeLabel && v === negativeLabel)
            return 'negative';
        // 評価ラベル以外の値は、中立の語だけを読み取る (肯定 / 否定を語で推定しない)。
        return inferPolarityFromLabel(v) === 'neutral' ? 'neutral' : null;
    };
    return {
        field,
        label,
        masterId: null,
        source,
        polarityLabels: {
            positive: positiveLabel || POLARITY_LABELS.positive,
            negative: negativeLabel || POLARITY_LABELS.negative,
            neutral: POLARITY_LABELS.neutral,
        },
        polarityOf,
        colorOf: (value) => {
            const p = polarityOf(value);
            return p ? POLARITY_DEFAULT_COLORS[p] : POLARITY_DEFAULT_COLORS.neutral;
        },
        labelOf: (value) => String(value ?? ''),
    };
}
/** 分類配列 (object-array) の itemFields の key 一覧 (無ければ空)。 */
function classificationItemKeys(schema) {
    const normalized = normalizeAiOutputFormat(schema);
    const arr = normalized.fields?.find((f) => f.kind === 'object-array');
    return arr && arr.kind === 'object-array' ? (arr.itemFields ?? []).map((f) => f.key) : [];
}
/**
 * 評価軸を解決する (docs/27 §9-2 (b))。
 * @param mastersById 軸が参照する M2 マスタ (id → master)。読込済みのものだけで良い。
 */
export function resolveEvaluationAxis(schema, axes, mastersById, display) {
    if (!schema)
        return null;
    const positiveLabel = schema.assessment?.positiveLabel?.trim() ?? '';
    const negativeLabel = schema.assessment?.negativeLabel?.trim() ?? '';
    const itemKeys = classificationItemKeys(schema);
    // 1. 明示設定。
    const configured = display.evaluationField?.trim();
    if (configured) {
        const axis = axes.find((a) => a.codeField === configured || a.key === configured);
        const master = axis?.masterId ? mastersById.get(axis.masterId) : undefined;
        if (master)
            return buildMasterEvaluation(axis.codeField, axis.label, master, 'configured');
        return buildLabelEvaluation(axis?.codeField ?? configured, axis?.label ?? '評価', positiveLabel, negativeLabel, 'configured');
    }
    // 2. 自動検出: polarity を持つ行があるマスタ、または行の名称がすべて評価の語のマスタを参照する軸。
    for (const axis of axes) {
        const master = axis.masterId ? mastersById.get(axis.masterId) : undefined;
        if (master &&
            ((master.rows ?? []).some((r) => r.polarity) || isPolarityLabelRows(master.rows))) {
            return buildMasterEvaluation(axis.codeField, axis.label, master, 'polarity-master');
        }
    }
    // 3. 自動検出: schema.assessment のラベル + assessment 列 (明示 itemFields / 派生列)。
    if (positiveLabel || negativeLabel) {
        const hasAssessmentColumn = itemKeys.length === 0 || itemKeys.includes('assessment');
        if (hasAssessmentColumn) {
            return buildLabelEvaluation('assessment', '評価', positiveLabel, negativeLabel, 'assessment-labels');
        }
        const axis = axes.find((a) => a.codeField === 'assessment');
        if (axis) {
            return buildLabelEvaluation(axis.codeField, axis.label, positiveLabel, negativeLabel, 'assessment-labels');
        }
    }
    return null;
}
/** 極性の件数を集計する。 */
export function countPolarity(values, evaluation, label = evaluation.label) {
    const counts = { positive: 0, negative: 0, neutral: 0, label };
    for (const v of values) {
        const p = evaluation.polarityOf(v);
        if (p)
            counts[p]++;
    }
    return counts;
}
//# sourceMappingURL=streetSurveyDisplay.js.map